/**
 * @kodaris/krubble-http
 *
 * HTTP client for making API requests with automatic base URL handling
 * and Kodaris API conveniences (CSRF tokens, credentials).
 *
 * ## Usage
 *
 * ### Web App (UMD)
 * ```html
 * <script src="krubble-http.umd.js"></script>
 * <script>
 *   // Configure once at startup
 *   kr.http.configure({
 *     baseUrl: 'https://kodaris.com',
 *     devUrl: 'https://localhost:4201'
 *   });
 *
 *   // Make requests
 *   kr.http.fetch({
 *     url: '/api/system/customer/list',
 *     method: 'POST',
 *     body: JSON.stringify({ page: 0, size: 20 })
 *   })
 *     .then(response => response.json())
 *     .then(data => console.log(data));
 * </script>
 * ```
 *
 * ### Angular / ESM
 * ```typescript
 * // app.config.ts - configure once at startup
 * import { KRHttp } from '@kodaris/krubble-http';
 *
 * KRHttp.configure({
 *   baseUrl: 'https://kodaris.com',
 *   devUrl: 'https://localhost:4201'
 * });
 *
 * // In a service or component
 * import { KRHttp } from '@kodaris/krubble-http';
 *
 * getCustomers() {
 *   return KRHttp.fetch({
 *     url: '/api/system/customer/list',
 *     method: 'POST',
 *     body: JSON.stringify({ page: 0, size: 20 })
 *   })
 *     .then(response => response.json());
 * }
 * ```
 *
 * ## URL Resolution
 * - On localhost: uses `devUrl` if configured, otherwise `baseUrl`
 * - On production: always uses `baseUrl`
 * - If URL starts with 'http', it's used as-is (no base URL prepended)
 */
/**
 * HTTP client with static methods for making API requests.
 * Automatically handles base URL resolution and Kodaris API conveniences.
 */
export class KRHttp {
    /**
       * Configure the HTTP client. Call once at application startup.
       *
       * @param config - Configuration options
       *
       * @example
       * KRHttp.configure({
       *   baseUrl: 'https://kodaris.com',
       *   devUrl: 'https://localhost:4201'
       * });
       */
    static configure(config) {
        if (config.baseUrl !== undefined) {
            KRHttp._baseUrl = config.baseUrl;
        }
        if (config.devUrl !== undefined) {
            KRHttp._devUrl = config.devUrl;
        }
    }
    /**
       * Returns the active base URL (devUrl on localhost, baseUrl otherwise).
       */
    static getBaseUrl() {
        var isDev = window.location.hostname === 'localhost' && window.location.port.startsWith('4');
        if (isDev && KRHttp._devUrl) {
            return KRHttp._devUrl;
        }
        return KRHttp._baseUrl;
    }
    /**
       * Make an HTTP request.
       *
       * @param options - Request options
       * @returns Promise resolving to the Response object
       *
       * @example
       * KRHttp.fetch({
       *   url: '/api/system/customer/list',
       *   method: 'POST',
       *   body: JSON.stringify({ page: 0, size: 20 })
       * })
       *   .then(response => response.json())
       *   .then(data => console.log(data));
       */
    static fetch(options) {
        let url = options.url;
        var isDev = window.location.hostname === 'localhost' && window.location.port.startsWith('4');
        let finalBaseUrl = KRHttp.getBaseUrl();
        // Add on the default base url if user
        // has not provided the full url
        if (url.indexOf('http') !== 0) {
            url = finalBaseUrl + url;
        }
        if (options.params) {
            url += '?';
            Object.keys(options.params).forEach((key, keyIdx) => {
                if (keyIdx > 0) {
                    url += '&';
                }
                url += `${key}=${options.params[key]}`;
            });
        }
        // Normalize headers to a plain object so we can set defaults
        var headers = {};
        if (options.headers) {
            if (options.headers instanceof Headers) {
                options.headers.forEach((v, k) => {
                    headers[k] = v;
                });
            }
            else if (Array.isArray(options.headers)) {
                options.headers.forEach(([k, v]) => {
                    headers[k] = v;
                });
            }
            else {
                Object.assign(headers, options.headers);
            }
        }
        // Auto-stringify plain object bodies and default Content-Type to JSON
        if (options.body && typeof options.body === 'object'
            && !(options.body instanceof ReadableStream)
            && !(options.body instanceof Blob)
            && !(options.body instanceof ArrayBuffer)
            && !(options.body instanceof FormData)
            && !(options.body instanceof URLSearchParams)) {
            options.body = JSON.stringify(options.body);
            if (!headers['Content-Type']) {
                headers['Content-Type'] = 'application/json';
            }
        }
        options.headers = headers;
        // Default credentials to include in dev mode
        if (isDev && !options.credentials) {
            options.credentials = 'include';
        }
        // User, and Account apis require CSRF tokens on non-GET requests
        let token;
        if (url.indexOf('/api/user/') > -1 || url.indexOf('/api/account/') > -1) {
            token = fetch(`${finalBaseUrl}/api/user/customer/authToken`, { credentials: 'include' });
        }
        else {
            token = Promise.resolve(null);
        }
        return token
            .then(res => res?.json())
            .then(res => {
            if (res?.data) {
                headers['X-CSRF-TOKEN'] = res.data;
            }
            return fetch(url, options);
        })
            .then(resp => {
            if (!resp.ok) {
                return Promise.reject(resp);
            }
            return resp;
        });
    }
}
/** Base URL for production requests */
KRHttp._baseUrl = '';
/** Base URL for development (localhost) */
KRHttp._devUrl = '';
//# sourceMappingURL=http.js.map